import * as uuid from 'uuid';

import { AlignmentRules } from '../../../planogram/models/alignmentRules';
import { Block } from '../../../planogram/models/block';
import { Bounding } from '../../../core/models/bounding';
import { Figure } from '../figures/figure';
import { EquipmentBase } from '../../../planogram/models/equipment-base';
import { EquipmentElement } from './equipmentElement';
import { EquipmentElements } from './equipmentElements';
import { Extension, ExtensionType, RoundingParams, HANGER_DISTANCE } from './extension';
import { ICanPutProductsResult } from '../../../planogram/models/canPutProductsResult';
import { Point } from '../../../core/models/point';
import { ProductGroup } from '../../../planogram/models/product-group';
import { Rectangle } from '../figures/rectangle';
import { RectangleSliced } from '../figures/rectangle-sliced';

import { Utils } from '../../../core/services/utils.service';
import * as jsts from 'jsts';
import { Trapeze } from '../figures/trapeze';
import { ensureShapeColorForElement } from '../elementFillColor';

export { HANGER_DISTANCE };

const EPS_3: number = 1e-3;
const GEOMETRY_FACTORY: jsts.geom.GeometryFactory = new jsts.geom.GeometryFactory();

// цей елемент відмальовуться на канві
export class Element {
	id: string;
	idx: number = null;
	parent: EquipmentElement = null;
	shapes: Array<Figure> = [];
	extensions: Array<Extension<any>> = [];
	depend: Element = null;
	stand: EquipmentBase;

	get allowedAutoLayout(): boolean {
		return true;
	}

	get canHaveSiblings(): boolean {
		return false;
	}

	get getBlockSegments(): Array<Bounding> {
		const segments: Array<Bounding> = this.getSiblingSegments(),
			blocks: Array<Block> = this.stand.blocks.filter((b: Block) =>
				[this, ...this.getSiblingElements].some((el: Element) => b.elements.includes(el))
			);

		return this.calcBlockSegments(blocks, segments);
	}

	get getBlockNotEmptySegments(): Array<Bounding> {
		const segments: Array<Bounding> = this.getSiblingSegments(),
			blocks: Array<Block> = this.stand.blocks
				.filter((b: Block) => [this, ...this.getSiblingElements].some((el: Element) => b.elements.includes(el)))
				.filter((b: Block) => !b.hasEmptySettings);

		return this.calcBlockSegments(blocks, segments);
	}

	get getSiblingElements(): Array<Element> {
		return [];
	}

	get products(): Array<Array<ProductGroup>> {
		return [];
	}

	get relatedElements(): Array<Element> {
		if (!this.stand) return [];

		return this.stand.allElements.filter((el: Element) => el.getDepends().includes(this));
	}

	get layoutBoundingX(): Bounding {
		return this.getBoundingX();
	}

	get layoutBoundingY(): Bounding {
		return this.getBoundingY();
	}

	get layoutBoundingZ(): Bounding {
		return this.getBoundingZ();
	}

	get layoutMeters(): number {
		const productsMaxWidth: Array<number> = this.products.map((group: Array<ProductGroup>) =>
			Utils.max(group.map((pg: ProductGroup) => pg.width))
		);

		return Utils.round(productsMaxWidth.reduce((a: number, b: number) => a + b, 0));
	}

	get linearMeters(): number {
		if (this.parent.is(EquipmentElements.HookWall, EquipmentElements.HookPanel)) {
			return 0; //TODO calculate hook rows
		}

		if (this.parent.is(EquipmentElements.PerforatedWall, EquipmentElements.Perforation)) {
			return 0; //TODO calculate hole rows
		}

		return 0;
	}

	get sortedProducts(): Array<ProductGroup> {
		return this.stand.products.filter((pg: ProductGroup) => pg.element == this);
	}

	get Depth(): number {
		const bounding: Bounding = this.getBoundingZ(),
			elementDepth: number = this.parent.type == EquipmentElements.AngleShelf
				? this.shapes[0].depth
				: Utils.round(bounding.max - bounding.min);

		if (!this.stand) return elementDepth;

		const backWall: Element = this.stand.allElements.find(
			(el: Element) => el.parent.type == 'BackWall' && el.depend?.id == this.id
		);

		if (backWall) return Utils.round(elementDepth - backWall.shapes[0].depth);

		return elementDepth;
	}

	get Height(): number {
		const bounding: Bounding = this.getBoundingY();

		return Utils.round(bounding.max - bounding.min);
	}

	get Width(): number {
		const bounding: Bounding = this.getBoundingX();

		return Utils.round(bounding.max - bounding.min);
	}

	get hangerDistance(): number {
		return this.showsHangers() ? HANGER_DISTANCE : 0;
	}

	get anchorYCoordinate(): number {
		return this.layoutBoundingY.max;
	}

	get anchorZCoordinate(): number {
		return this.layoutBoundingZ.max;
	}

	get zCoordinates(): Array<Point> {
		const boundingX: Bounding = this.layoutBoundingX;
		const boundingZ: Bounding = this.layoutBoundingZ;

		return [
			{ x: boundingX.min, y: boundingZ.max },
			{ x: boundingX.max, y: boundingZ.max },
			{ x: boundingX.max, y: boundingZ.min },
			{ x: boundingX.min, y: boundingZ.min },
			{ x: boundingX.min, y: boundingZ.max }
		];
	}

	get center(): Point {
		const boundingX: Bounding = this.getBoundingX();
		const boundingY: Bounding = this.getBoundingY();
		const boundingZ: Bounding = this.getBoundingZ();

		return {
			x: Utils.round((boundingX.max + boundingX.min) / 2),
			y: Utils.round((boundingY.max + boundingY.min) / 2),
			z: Utils.round((boundingZ.max + boundingZ.min) / 2)
		};
	}

	static alignmentRules(groups: Array<Array<ProductGroup>>, idx: number, segment: Bounding): AlignmentRules {
		const calculateDefaultX = (min: number): number => {
			const productsWidth = Utils.round(
					groups.reduce((a: number, b: Array<ProductGroup>) => a + Element.calculateGroupWidth(b), 0)
				),
				remainingGroups = groups.filter((group: Array<ProductGroup>, groupIdx: number) => groupIdx > idx),
				remainingWidth = Utils.round(
					remainingGroups.reduce(
						(a: number, b: Array<ProductGroup>) => a + Element.calculateGroupWidth(b),
						0
					)
				);

			if (productsWidth < Utils.round(segment.max - segment.min)) {
				if (remainingWidth <= Utils.round(segment.max - Element.calculateGroupMax(groups[idx]))) {
					return Utils.max([min, Element.calculateGroupMin(groups[idx])]);
				} else {
					return Utils.max([min, segment.max - remainingWidth - Element.calculateGroupWidth(groups[idx])]);
				}
			} else {
				return min;
			}
		};

		const checkAffiliation = (limits: Bounding): boolean => {
			const checkMinPoint: boolean = segment.min != limits.min,
				checkMaxPoint: boolean = segment.max != limits.max,
				groupPoint: number = Element.calculateGroupMin(groups[idx]),
				filter: Array<boolean> = [
					!checkMinPoint ? groupPoint < segment.max : groupPoint >= segment.min && groupPoint < segment.max,
					!checkMaxPoint ? groupPoint >= segment.min : groupPoint >= segment.min && groupPoint < segment.max
				];
			return filter.some((condition: boolean) => condition);
		};

		return {
			default: {
				startPoint: (prevProd: Bounding = null): number => {
					const startPoint: number = prevProd ? Utils.max([prevProd.max, segment.min]) : segment.min;
					return Utils.round(calculateDefaultX(startPoint));
				},
				nextPoint: (): number => {
					return Utils.round(calculateDefaultX(Element.calculateGroupMax(groups[idx - 1])));
				},
				checkAffiliation,
				checkDependingPoint: (): number => Element.calculateGroupMin(groups[idx])
			},
			left: {
				startPoint: (prevProd: Bounding = null): number => {
					return prevProd ? Utils.max([prevProd.max, segment.min]) : segment.min;
				},
				nextPoint: (): number => {
					return Utils.max(
						groups
							.filter((group: Array<ProductGroup>, groupIdx: number) => groupIdx < idx)
							.map((group: Array<ProductGroup>) => Element.calculateGroupMax(group))
					);
				},
				checkAffiliation,
				checkDependingPoint: (): number => Element.calculateGroupMin(groups[idx])
			},
			right: {
				startPoint: (prevProd: Bounding = null): number => {
					const startPoint: number = prevProd ? Utils.min([prevProd.min, segment.max]) : segment.max;
					return Utils.round(startPoint - Element.calculateGroupWidth(groups[idx]));
				},
				nextPoint: (): number => {
					const point: number = Utils.min(
						groups
							.filter((group: Array<ProductGroup>, groupIdx: number) => groupIdx < idx)
							.map((group: Array<ProductGroup>) => Element.calculateGroupMin(group))
					);
					return Utils.round(point - Element.calculateGroupWidth(groups[idx]));
				},
				checkAffiliation: (limits: Bounding): boolean => {
					const checkMinPoint: boolean = segment.min != limits.min,
						checkMaxPoint: boolean = segment.max != limits.max,
						groupPoint: number = Element.calculateGroupMax(groups[idx]),
						filter: Array<boolean> = [
							!checkMinPoint
								? groupPoint <= segment.max
								: groupPoint > segment.min && groupPoint <= segment.max,
							!checkMaxPoint
								? groupPoint > segment.min
								: groupPoint > segment.min && groupPoint <= segment.max
						];
					return filter.some((condition) => condition);
				},
				checkDependingPoint: (): number => Element.calculateGroupMin(groups[idx])
			},
			stretch: {
				startPoint: (prevProd: Bounding = null): number => {
					const startPoint: number = Utils.round(prevProd ? Utils.max([prevProd.max, segment.min]) : segment.min),
						groupWidth: number = Element.calculateGroupWidth(groups[idx]),
						retreat: number = groups.length > 1 ? 0 : (segment.max - startPoint) / 2 - groupWidth / 2;
					return retreat < 0 ? startPoint : Utils.round(startPoint + retreat);
				},
				nextPoint: (): number => {
					const point: number = Utils.max(
							groups
								.filter((group: Array<ProductGroup>, groupIdx: number) => groupIdx < idx)
								.map((group: Array<ProductGroup>) => Element.calculateGroupMax(group))
						),
						remainingGroups = groups.filter(
							(group: Array<ProductGroup>, groupIdx: number) => groupIdx >= idx
						),
						pgWidth: number = Utils.round(
							remainingGroups.reduce((a: number, b: Array<ProductGroup>) => {
								return a + Element.calculateGroupWidth(b);
							}, 0)
						);
					let retreat: number = Utils.round((segment.max - point - pgWidth) / remainingGroups.length);
					if (pgWidth + retreat * remainingGroups.length > segment.max - point) retreat -= EPS_3;
					return retreat < 0 ? point : Utils.round(point + retreat);
				},
				checkAffiliation,
				checkDependingPoint: (): number => Element.calculateGroupMin(groups[idx])
			}
		};
	}

	static calculateGroupMax(group: Array<ProductGroup>): number {
		if (!group.length) return 0;

		return Utils.round(Utils.max(group.map((pg: ProductGroup) => pg.getBoundingX().max)));
	}

	static calculateGroupMin(group: Array<ProductGroup>): number {
		if (!group.length) return 0;

		return Utils.round(Utils.min(group.map((pg: ProductGroup) => pg.getBoundingX().min)));
	}

	static calculateGroupWidth(group: Array<ProductGroup>): number {
		if (!group.length) return 0;

		return Utils.round(Element.calculateGroupMax(group) - Element.calculateGroupMin(group));
	}

	constructor(
		parent: EquipmentElement,
		shapes: Array<Figure>,
		extensions: Array<Extension<any>>,
		depend: Element,
		id?: string,
		idx?: number
	) {
		this.id = id || uuid.v4();
		Object.assign(this, { parent, shapes, extensions, depend, idx });
		ensureShapeColorForElement(this);
	}

	addProduct(pg: ProductGroup): void {
		pg.element = this;
	}

	calcBlockSegments(blocks: Array<Block>, segments: Array<Bounding>): Array<Bounding> {
		const clippedSegments: Array<Bounding> = [];

		if (!blocks.length) return segments;

		const blockSegmentsPoints: Array<number> = blocks
			.map((block: Block) => {
				return {
					min: Utils.round(Utils.max([block.begin[0].x, segments[0].min])),
					max: Utils.round(Utils.min([block.begin[0].x + block.width, segments[segments.length - 1].max]))
				};
			})
			.sort((a: Bounding, b: Bounding) => a.min - b.min)
			.reduce((result: Array<number>, limit: Bounding) => [...result, limit.min, limit.max], []);

		segments.forEach((limit: Bounding) => {
			let prevPoint: number = limit.min;
			blockSegmentsPoints.forEach((point: number) => {
				if (prevPoint < point && point < limit.max) {
					clippedSegments.push({ min: prevPoint, max: point });
					prevPoint = point;
				}
			});
			if (prevPoint < limit.max) {
				clippedSegments.push({ min: prevPoint, max: limit.max });
			}
		});

		return clippedSegments;
	}

	canDrop(product: ProductGroup): boolean {
		return true;
	}

	copy(): Element {
		const shapes: Array<Figure> = this.shapes.map((shape: Figure) => shape.copy()),
			extensions: Array<Extension<any>> = this.extensions.map((extension: Extension<any>) => extension.copy());

		return new Element(this.parent, shapes, extensions, this.depend, this.id, this.idx);
	}

	duplicate(): Element {
		const shapes: Array<Figure> = this.shapes.map((shape: Figure) => shape.copy()),
			extensions: Array<Extension<any>> = this.extensions.map((extension: Extension<any>) => extension.copy());

		return new Element(this.parent, shapes, extensions, this.depend);
	}

	getAbsoluteBoundingZ(): Bounding {
		if (this.shapes[0].type == 'trapeze' || this.shapes[0].type == 'cylinder') return this.getBoundingZ();

		const shapesInfo: Array<Bounding> = this.shapes.map((shape: Figure) =>
			(shape as Rectangle | RectangleSliced).getAbsoluteBoundingZ()
		);

		return {
			min: Utils.round(Math.min(...shapesInfo.map((shape: Bounding) => shape.min))),
			max: Utils.round(Math.max(...shapesInfo.map((shape: Bounding) => shape.max)))
		};
	}

	getBoundingX(usable_area: boolean = false): Bounding {
		const shapesInfo: Array<Bounding> = this.shapes.map((shape: Figure) => shape.getBoundingX(usable_area));

		if (usable_area && this.isExtensionActive(ExtensionType.Rounding)) {
			const rounding: Extension<RoundingParams> = this.getExtension<RoundingParams>(ExtensionType.Rounding);
			const leftRadius: number = Utils.convertPercentToRadius(rounding.params.leftSideRadius, this);
			const rightRadius: number = Utils.convertPercentToRadius(rounding.params.rightSideRadius, this);

			return {
				min: Utils.round(Math.min(...shapesInfo.map((shape: Bounding) => shape.min)) + leftRadius),
				max: Utils.round(Math.max(...shapesInfo.map((shape: Bounding) => shape.max)) - rightRadius)
			};
		}

		return {
			min: Utils.round(Math.min(...shapesInfo.map((shape: Bounding) => shape.min))),
			max: Utils.round(Math.max(...shapesInfo.map((shape: Bounding) => shape.max)))
		};
	}

	getBoundingY(): Bounding {
		const shapesInfo: Array<Bounding> = this.shapes.map((shape: Figure) => shape.getBoundingY());

		return {
			min: Utils.round(Math.min(...shapesInfo.map((shape: Bounding) => shape.min))),
			max: Utils.round(Math.max(...shapesInfo.map((shape: Bounding) => shape.max)))
		};
	}

	getBoundingZ(): Bounding {
		const shapesInfo: Array<Bounding> = this.shapes.map((shape: Figure) => shape.getBoundingZ());

		return {
			min: Utils.round(Math.min(...shapesInfo.map((shape: Bounding) => shape.min))),
			max: Utils.round(Math.max(...shapesInfo.map((shape: Bounding) => shape.max)))
		};
	}

	getDepends(): Array<Element> {
		return this.depend ? [this.depend, ...this.depend.getDepends()] : [];
	}

	getDroppedProduct(...params): ProductGroup {
		return null;
	}

	getMaxPlacementDepth(productGroup: ProductGroup, allowZero: boolean = false) {
		if (this.parent.is(EquipmentElements.AngleShelf)) {
			return Math.max(
				Math.floor((productGroup.dependProduct?.Depth || this.Depth) / productGroup.ItemDepth),
				allowZero ? 0 : 1
			);
		}

		const productId = productGroup.getIdHash();
		const { min: thisMinZ, max: thisMaxZ } = productGroup.dependProduct
			? productGroup.dependProduct.getBoundingZ()
			: this.shapes[0]?.type === 'trapeze'
				? (this.shapes[0] as Trapeze).getBoundingZBetween(productGroup.getBoundingX())
				: this.layoutBoundingZ;
		const productXPolygon = productGroup.xPolygon;
		const productPivotZ = productGroup.begin.z ?? 0;

		const availableDepthBoundaries = this.stand.allElements
			// Hooks exclusion should not be needed but as per client needs we exclude them
			.filter(
				(element) => element !== this && !element.parent.is(EquipmentElements.Showcase, EquipmentElements.Hook)
			)
			.flatMap((element) => element.shapes)
			.reduce(
				(boundaries, shape) => {
					// TODO: Move this to shape classes
					const { min: xMin, max: xMax } = shape.getBoundingX();
					const { min: yMin, max: yMax } = shape.getBoundingY();

					const shapeXPolygon = GEOMETRY_FACTORY.createPolygon([
						new jsts.geom.Coordinate(xMin, yMin),
						new jsts.geom.Coordinate(xMax, yMin),
						new jsts.geom.Coordinate(xMax, yMax),
						new jsts.geom.Coordinate(xMin, yMax),
						new jsts.geom.Coordinate(xMin, yMin)
					]);

					if (!shapeXPolygon.intersects(productXPolygon) || shapeXPolygon.touches(productXPolygon)) {
						return boundaries;
					}

					const { min: zMin, max: zMax } = shape.getBoundingZ();

					if (zMax <= productPivotZ) {
						boundaries.min = Math.max(boundaries.min, zMax);
					}

					if (zMin >= productPivotZ) {
						boundaries.max = Math.min(boundaries.max, zMin);
					}

					return boundaries;
				},
				{
					min: thisMinZ,
					max: thisMaxZ
				}
			);

		const availableDepth = availableDepthBoundaries.max - availableDepthBoundaries.min;
		const occupiedRanges = this.products.flat().reduce((occupiedRanges, currentGroup) => {
			if (currentGroup.getIdHash() === productId) {
				return occupiedRanges;
			}

			if (
				!this.stand.arbitraryLayout &&
				Math.abs(currentGroup.begin.x - productGroup.begin.x) > EPS_3
			) {
				return occupiedRanges;
			}

			const { min: minZ, max: maxZ } = currentGroup.getBoundingZ();

			if (minZ >= availableDepthBoundaries.max) {
				return occupiedRanges;
			}

			if (maxZ <= availableDepthBoundaries.min) {
				return occupiedRanges;
			}

			const currentGroupXPolygon = currentGroup.xPolygon;

			if (!currentGroupXPolygon.intersects(productXPolygon) || currentGroupXPolygon.touches(productXPolygon)) {
				return occupiedRanges;
			}

			occupiedRanges.push({
				min: Math.max(minZ, availableDepthBoundaries.min),
				max: Math.min(maxZ, availableDepthBoundaries.max)
			});

			return occupiedRanges;
		}, [] as Array<Bounding>);

		occupiedRanges.sort(({ min: aMin }, { min: bMin }) => aMin - bMin);

		const occupiedSpace = occupiedRanges
			.reduce((mergedRanges, range) => {
				const lastRange = mergedRanges[mergedRanges.length - 1];

				if (lastRange && range.min <= lastRange.max) {
					lastRange.max = Math.max(lastRange.max, range.max);

					return mergedRanges;
				}

				mergedRanges.push({ min: range.min, max: range.max });

				return mergedRanges;
			}, [] as Array<Bounding>)
			.reduce((occupiedSpace, { max, min }) => occupiedSpace + (max - min), 0);

		return Math.max(
			Math.floor(Utils.round(availableDepth - occupiedSpace) / productGroup.ItemDepth),
			allowZero ? 0 : 1
		);
	}

	getMaxPlacementHeight(productGroup: ProductGroup, allowZero: boolean = false) {
		// TODO: Refactor this so that we calculate the actual free space remaining
		// Current behavior is wrong. In case we have multiple producs stackes this calculates how many target products fit the depend product height (not free space)
		// Granted currently this is not an issue as this is only actually used for boxes where we cannot put products on top of each other
		return Math.max(
			Math.floor((productGroup.dependProduct?.Height || this.Height) / productGroup.ItemHeight),
			allowZero ? 0 : 1
		);
	}

	getSiblingSegments(siblingsPoint: boolean = true, includeBoxes: boolean = true): Array<Bounding> {
		const siblings: Array<Element> = this.getSiblingElements;
		return [
			{
				min: Utils.min(
					[this, ...siblings].map((el: Element) => el.getBoundingX(this.stand.alignment !== 'default').min)
				),
				max: Utils.max(
					[this, ...siblings].map((el: Element) => el.getBoundingX(this.stand.alignment !== 'default').max)
				)
			}
		];
	}

	getExtension<T>(type: ExtensionType): Extension<T> {
		return this.extensions.find((extension: Extension<any>) => extension.type == type) || null;
	}

	hasExtension(type: ExtensionType): boolean {
		return this.extensions.some((extension: Extension<any>) => extension.type == type);
	}

	is(...classes: Array<any>): boolean {
		return classes.some((item: any) => this instanceof item);
	}

	isExtensionActive(type: ExtensionType): boolean {
		const extension: Extension<any> = this.getExtension<any>(type);

		return extension && extension.active;
	}

	showsHangers(): boolean {
		const hangBar = this.getHangBar();

		if (!hangBar) {
			return false;
		}

		return !hangBar.hasExtension(ExtensionType.Hangers) || hangBar.isExtensionActive(ExtensionType.Hangers);
	}

	private getHangBar(): Element | null {
		if (this.parent.is(EquipmentElements.HangBar)) {
			return this;
		}

		if (this.depend?.parent.is(EquipmentElements.HangBar)) {
			return this.depend;
		}

		return null;
	}

	normalize(...params): void {}

	public canMoveProductGroup(productGroup: ProductGroup, x?: number, y?: number, z?: number): boolean {
		return false;
	}

	public canPutProductGroups(products: Array<ProductGroup>, point: Point): ICanPutProductsResult {
		return { success: false };
	}

	public canRotateProductGroup(productGroup: ProductGroup): boolean {
		return false;
	}

	public findDepend(...types: Array<EquipmentElements>): Element | null {
		if (!this.depend) return this.relatedElements.find((el: Element) => el.parent.is(...types)) || null;
		if (this.depend.parent.is(...types)) return this.depend;
		return this.depend.findDepend(...types);
	}

	public putProductGroups(products: Array<ProductGroup>, list: Array<Array<ProductGroup>>, point: Point): void {}

	public setProductPlacementHeight(pg: ProductGroup, placementHeight: number): void {
		pg.placementHeight = placementHeight;
	}

	public shiftProductGroups(
		activeGroups: Array<Array<ProductGroup>>,
		excludeProducts: Array<ProductGroup> = []
	): void {}
}
